import type { Composer } from 'vue-i18n'
import type { RouteLocationRaw } from 'vue-router'
import type { BadgeTone } from './badge-variant'
import { formatDay, formatDayRange } from './metric-format'
import { metricName as nameOf } from './analyst'
import type { ObjectiveStatus, Origin, ReportKind, TaskPriority, TaskStatus } from '@/types/intelligence'

/**
 * How the action loop is written, in the reader's language (P5, P6): the
 * API sends codes, figures and days, and the words, numbers and dates are
 * made here. A follow-up's title and note are their author's own words and
 * are shown as written.
 */

type Translate = Composer['t']

export const TASK_PRIORITIES: TaskPriority[] = ['LOW', 'MEDIUM', 'HIGH', 'URGENT']

/** The badge always carries its word, so colour is never the only signal. */
export const TASK_PRIORITY_TONE: Record<TaskPriority, BadgeTone> = { LOW: 'neutral', MEDIUM: 'warning', HIGH: 'conflict', URGENT: 'danger' }
export const TASK_STATUS_TONE: Record<TaskStatus, BadgeTone> = { OPEN: 'neutral', IN_PROGRESS: 'warning', COMPLETED: 'accent', DISMISSED: 'neutral' }
export const OBJECTIVE_STATUS_TONE: Record<ObjectiveStatus, BadgeTone> = {
  ON_TRACK: 'accent',
  AT_RISK: 'warning',
  BEHIND: 'conflict',
  ACHIEVED: 'accent',
  MISSED: 'danger',
}

/** A metric's name, as the Overview's cards call it: one of the five, or a care journey share an objective is set on (DECISIONS.md #45). */
export const metricName = (t: Translate, metricId: string): string => nameOf(metricId, t)

/** A metric's value in its unit: "38 min" to a tenth, a share in percent to a tenth ("76.4%"), or a count. */
export const formatValue = (t: Translate, value: number, unit: 'COUNT' | 'MINUTES' | 'PERCENT', locale: string): string => {
  if (unit === 'PERCENT') return new Intl.NumberFormat(locale, { style: 'percent', maximumFractionDigits: 1 }).format(value / 100)
  return unit === 'MINUTES'
    ? t('overview.units.minutes', { value: new Intl.NumberFormat(locale, { maximumFractionDigits: 1 }).format(value) })
    : new Intl.NumberFormat(locale, { maximumFractionDigits: 1 }).format(value)
}

/** A calendar day with its year, as the reader writes dates: kept here, where the action loop's screens look for it. */
export { formatDay }

/** An instant as a day and time in the reader's own zone. */
export const formatInstant = (instant: string, locale: string): string =>
  new Intl.DateTimeFormat(locale, { dateStyle: 'medium', timeStyle: 'short' }).format(new Date(instant))

/** The day an instant fell on, in the reader's own zone: not the UTC day its ISO string starts with. */
export const formatInstantDay = (instant: string, locale: string): string =>
  new Intl.DateTimeFormat(locale, { day: 'numeric', month: 'short', year: 'numeric' }).format(new Date(instant))

/** A share of the way, whole percent: "40%". */
export const formatShare = (share: number, locale: string): string =>
  new Intl.NumberFormat(locale, { style: 'percent', maximumFractionDigits: 0 }).format(share)

/** A report's kind and the days it covers: "Weekly operations review · 21 – 27 Sep". */
export const reportLabel = (t: Translate, kind: ReportKind, first: string, last: string, locale: string): string =>
  t('reports.label', { kind: t(`reports.kinds.${kind}.name`), period: formatDayRange(first, last, locale) })

/** What a follow-up or share starts from, by name alone: an opportunity by its finding, an objective by its title, a report by its kind. */
export const originName = (t: Translate, origin: Origin, locale: string): string => {
  if (origin.kind === 'OPPORTUNITY') {
    const key = `opportunities.metrics.${origin.metricId}.title`
    return t(key) === key ? origin.metricId : t(key)
  }
  if (origin.kind === 'OBJECTIVE') return origin.title
  return reportLabel(t, origin.reportKind, origin.periodFirst, origin.periodLast, locale)
}

/** The same, saying what kind it is: "Opportunity: Waiting time increased". */
export const originLabel = (t: Translate, origin: Origin, locale: string): string =>
  t(`actions.origin.${origin.kind}`, { name: originName(t, origin, locale) })

/** Where an origin is opened. An objective is found on the Objectives page, whatever its state. */
export const originRoute = (origin: Pick<Origin, 'kind' | 'id'>, workspaceId: string): RouteLocationRaw => {
  if (origin.kind === 'OPPORTUNITY') return { name: 'workspace-opportunity', params: { workspaceId, opportunityId: origin.id } }
  if (origin.kind === 'OBJECTIVE') return { name: 'workspace-objectives', params: { workspaceId }, query: { status: 'ALL' }, hash: `#objective-${origin.id}` }
  return { name: 'workspace-report', params: { workspaceId, reportId: origin.id } }
}

/** A follow-up's due date in words: none, due, or overdue (P6 §3.1: never overdue without one). */
export const dueText = (t: Translate, task: { dueDate: string | null; overdue: boolean }, locale: string): string => {
  if (!task.dueDate) return t('actions.task.noDueDate')
  return task.overdue ? t('actions.task.overdueSince', { date: formatDay(task.dueDate, locale) }) : t('actions.task.due', { date: formatDay(task.dueDate, locale) })
}

/**
 * Why the API refused, in the reader's language: its error code, never its
 * English message. A code with no sentence of its own reads as a general failure.
 */
export const actionErrorText = (t: Translate, te: (key: string) => boolean, error: unknown): string => {
  const code = typeof error === 'object' && error !== null && 'code' in error ? String((error as { code: unknown }).code) : ''
  return code && te(`actions.errors.${code}`) ? t(`actions.errors.${code}`) : t('actions.errors.generic')
}
